import React, { useState, useContext } from "react";
import TableBuilder from "./TableBuilder";
import { PropertyContext } from "../../../contexts/PropertyContext";

const TableProperties = ({ selectedDiv, handleChange, searchTerm }) => {
  const [showTableBuilder, setShowTableBuilder] = useState(false);
  const {
    renderMarginProperties,
    renderPaddingProperties,
    renderBorderProperties,
    renderPositionProperties,
    renderBackgroundProperties,
    renderGradientProperties,
  } = useContext(PropertyContext);

  const handleTableBuilderData = (tableData, colspanRowspanData) => {
    // Convert tableData to the format expected by the existing component
    const headers = tableData[0].join(",");
    const data = tableData
      .slice(1)
      .map((row) => row.join(","))
      .join(";");

    // Update the selectedDiv with the new table data
    handleChange({ target: { name: "headers", value: headers } });
    handleChange({ target: { name: "data", value: data } });
    handleChange({
      target: { name: "colspanRowspan", value: colspanRowspanData },
    });

    setShowTableBuilder(false);
  };
  const renderTableBuilder = () => {
    if (
      searchTerm &&
      !"Table Builder".toLowerCase().includes(searchTerm.toLowerCase())
    ) {
      return null;
    }
    return (
      <div key="tableBuilder" style={styles.inputGroup}>
        <button
          onClick={() => setShowTableBuilder(!showTableBuilder)}
          style={styles.button}
        >
          {showTableBuilder ? "Hide Table Builder" : "Show Table Builder"}
        </button>
        {showTableBuilder && (
          <TableBuilder
            initialData={
              selectedDiv
                ? {
                    headers: selectedDiv.headers,
                    data: selectedDiv.data,
                    colspanRowspan: selectedDiv.colspanRowspan,
                  }
                : null
            }
            onDataChange={handleTableBuilderData}
            onClose={() => setShowTableBuilder(false)}
          />
        )}
      </div>
    );
  };

  const renderInput = (label, name, type = "text", additionalProps = {}) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <input
          type={type}
          name={name}
          value={selectedDiv ? selectedDiv[name] : ""}
          onChange={handleChange}
          style={styles.input}
          {...additionalProps}
        />
      </div>
    );
  };
  const renderSelect = (label, name, options) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <select
          name={name}
          value={selectedDiv ? selectedDiv[name] : ""}
          onChange={handleChange}
          style={styles.select}
        >
          <option value="">Select</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  };
  const renderMutableInput = (label, name, array) => {
    if (searchTerm && !label.toLowerCase().includes(searchTerm.toLowerCase())) {
      return null;
    }
    return (
      <div key={name} style={styles.inputGroup}>
        <label style={styles.label}>{label}: </label>
        <div style={styles.widthHeightInputGroup}>
          <select
            name={name}
            value={selectedDiv ? selectedDiv[name] : ""}
            onChange={handleChange}
            style={styles.select}
          >
            {array.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <input
            type="text"
            name={name}
            value={selectedDiv ? selectedDiv[name] : ""}
            onChange={handleChange}
            style={styles.input}
          />
        </div>
      </div>
    );
  };

  const allInputs = [
    <h4 key="table-structure" style={styles.heading}>
      Table Structure
    </h4>,
    renderTableBuilder(),

    <h4 key="box-model" style={styles.heading}>
      Box Model Properties
    </h4>,
    renderMutableInput("Width", "width", [
      "auto",
      "fit-content",
      "max-content",
      "min-content",
    ]),
    renderMutableInput("Height", "height", [
      "auto",
      "fit-content",
      "max-content",
      "min-content",
    ]),
    renderMarginProperties(renderMutableInput),
    renderPaddingProperties(renderMutableInput),
    renderBorderProperties(renderMutableInput, renderInput, renderSelect),

    <h4 key="positioning" style={styles.heading}>
      Positioning Properties
    </h4>,
    renderPositionProperties(renderMutableInput, renderInput, renderSelect),

    <h4 key="visual" style={styles.heading}>
      Visual Properties
    </h4>,
    renderInput("Color", "color", "color"),
    renderInput("Background Color", "backgroundColor", "color"),
    renderBackgroundProperties(
      renderSelect,
      searchTerm,
      handleChange,
      selectedDiv
    ),
    renderGradientProperties(
      renderSelect,
      searchTerm,
      renderInput,
      selectedDiv
    ),
    renderMutableInput("Box Shadow", "boxShadow", [
      "none",
      "2px 4px 10px #000000",
    ]),
    renderInput("Opacity", "opacity", "number", { min: 0, max: 1, step: 0.01 }),
    renderSelect("Overflow", "overflow", [
      "visible",
      "hidden",
      "scroll",
      "auto",
    ]),
    renderSelect("Overflow-X", "overflowX", [
      "visible",
      "hidden",
      "scroll",
      "auto",
    ]),
    renderSelect("Overflow-Y", "overflowY", [
      "visible",
      "hidden",
      "scroll",
      "auto",
    ]),

    <h4 key="table-specific" style={styles.heading}>
      Table-Specific Properties
    </h4>,
    renderSelect("Caption Side", "captionSide", ["top", "bottom"]),
    renderSelect("Empty Cells", "emptyCells", ["show", "hide"]),
    renderSelect("Table Layout", "tableLayout", ["auto", "fixed"]),

    <h4 key="cell" style={styles.heading}>
      Cell Properties
    </h4>,
    renderInput("Cell Padding", "cellPadding"),
    renderInput("Border Cell", "borderCell"),
    renderInput("Border Top Cell", "borderTopCell"),
    renderInput("Border Bottom Cell", "borderBottomCell"),
    renderInput("Border Left Cell", "borderLeftCell"),
    renderInput("Border Right Cell", "borderRightCell"),
    renderInput("Border Radius Cell", "borderRadiusCell"),
    renderInput("Cell Color", "Cellcolor", "color"),
    renderInput("Cell Background Color", "CellbackgroundColor", "color"),

    <h4 key="cell-text" style={styles.heading}>
      Cell Text Properties
    </h4>,
    renderMutableInput("Font Size Cell", "fontSizeCell", [
      "16px",
      "2em",
      "1rem",
      "75%",
      "1vw",
    ]),
    renderSelect("Font Family Cell", "fontFamilyCell", [
      "Arial",
      "Helvetica",
      "Times New Roman",
      "Courier New",
      "Verdana",
      "Georgia",
      "Palatino",
      "Garamond",
      "Comic Sans MS",
      "Trebuchet MS",
      "Arial Black",
      "Impact",
      "Tahoma",
      "Lucida Sans Unicode",
      "Lucida Grande",
      "Century Gothic",
      "Frank Ruhl",
      "Segoe UI",
      "Gill Sans",
      "Myriad Pro",
      "Futura",
      "Baskerville",
      "Didot",
      "Optima",
      "Caslon",
      "Cambria",
      "Consolas",
      "Menlo",
      "Andale Mono",
      "Inconsolata",
      "Source Sans Pro",
      "Roboto",
      "Open Sans",
      "Lato",
      "Montserrat",
      "Raleway",
      "Oswald",
      "Poppins",
      "Merriweather",
      "PT Sans",
      "Playfair Display",
      "Nunito",
      "Rubik",
      "Josefin Sans",
      "Quicksand",
      "Droid Sans",
      "Noto Sans",
      "Exo 2",
      "Work Sans",
      "Overpass",
      "Alegreya",
      "Bebas Neue",
      "Hind",
      "Anton",
      "Lora",
      "DM Sans",
      "Barlow",
      "Titillium Web",
      "Suisse Int'l",
      "Noto Serif",
      "Tisa",
      "Muli",
      "Arimo",
      "Karla",
      "Zilla Slab",
      "Space Mono",
      "Libre Baskerville",
      "Fira Sans",
      "Archivo Narrow",
      "Varela Round",
      "Oxygen",
      "Catamaran",
      "Cairo",
      "Dosis",
      "Kreon",
      "Bitter",
      "Candal",
      "Averia Serif Libre",
      "Arapey",
      "Hind Guntur",
      "Saira",
      "Titillium",
      "Dancing Script",
      "Satisfy",
      "Raleway Dots",
      "Courgette",
      "Lobster",
      "Pacifico",
      "Shadows Into Light",
      "Sacramento",
      "Rock Salt",
      "Yellowtail",
    ]),
    renderSelect("Font Weight Cell", "fontWeightCell", [
      "normal",
      "bold",
      "100",
      "200",
      "300",
      "400",
      "500",
      "600",
      "700",
      "800",
      "900",
    ]),
    renderSelect("Font Style Cell", "fontStyleCell", [
      "normal",
      "italic",
      "oblique",
    ]),
    renderSelect("Text Align Cell", "textAlignCell", [
      "left",
      "center",
      "right",
      "justify",
    ]),
    renderSelect("Text Decoration Cell", "textDecorationCell", [
      "none",
      "underline",
      "overline",
      "line-through",
    ]),
    renderSelect("Text Transform Cell", "textTransformCell", [
      "none",
      "capitalize",
      "uppercase",
      "lowercase",
    ]),

    <h4 key="header" style={styles.heading}>
      Header Properties
    </h4>,
    renderInput("Header Padding", "headerPadding"),
    renderInput("Border Header", "borderHeader"),
    renderInput("Border Top Header", "borderTopHeader"),
    renderInput("Border Bottom Header", "borderBottomHeader"),
    renderInput("Border Left Header", "borderLeftHeader"),
    renderInput("Border Right Header", "borderRightHeader"),
    renderInput("Border Radius Header", "borderRadiusHeader"),
    renderInput("Header Color", "Headercolor", "color"),
    renderInput("Header Background Color", "HeaderbackgroundColor", "color"),

    <h4 key="header-text" style={styles.heading}>
      Header Text Properties
    </h4>,
    renderMutableInput("Font Size Header", "fontSizeHeader", [
      "16px",
      "2em",
      "1rem",
      "75%",
      "1vw",
    ]),
    renderSelect("Font Family Header", "fontFamilyHeader", [
      "Arial",
      "Helvetica",
      "Times New Roman",
      "Courier New",
      "Verdana",
      "Georgia",
      "Palatino",
      "Garamond",
      "Comic Sans MS",
      "Trebuchet MS",
      "Arial Black",
      "Impact",
      "Tahoma",
      "Lucida Sans Unicode",
      "Lucida Grande",
      "Century Gothic",
      "Frank Ruhl",
      "Segoe UI",
      "Gill Sans",
      "Myriad Pro",
      "Futura",
      "Baskerville",
      "Didot",
      "Optima",
      "Caslon",
      "Cambria",
      "Consolas",
      "Menlo",
      "Andale Mono",
      "Inconsolata",
      "Source Sans Pro",
      "Roboto",
      "Open Sans",
      "Lato",
      "Montserrat",
      "Raleway",
      "Oswald",
      "Poppins",
      "Merriweather",
      "PT Sans",
      "Playfair Display",
      "Nunito",
      "Rubik",
      "Josefin Sans",
      "Quicksand",
      "Droid Sans",
      "Noto Sans",
      "Exo 2",
      "Work Sans",
      "Overpass",
      "Alegreya",
      "Bebas Neue",
      "Hind",
      "Anton",
      "Lora",
      "DM Sans",
      "Barlow",
      "Titillium Web",
      "Suisse Int'l",
      "Noto Serif",
      "Tisa",
      "Muli",
      "Arimo",
      "Karla",
      "Zilla Slab",
      "Space Mono",
      "Libre Baskerville",
      "Fira Sans",
      "Archivo Narrow",
      "Varela Round",
      "Oxygen",
      "Catamaran",
      "Cairo",
      "Dosis",
      "Kreon",
      "Bitter",
      "Candal",
      "Averia Serif Libre",
      "Arapey",
      "Hind Guntur",
      "Saira",
      "Titillium",
      "Dancing Script",
      "Satisfy",
      "Raleway Dots",
      "Courgette",
      "Lobster",
      "Pacifico",
      "Shadows Into Light",
      "Sacramento",
      "Rock Salt",
      "Yellowtail",
    ]),
    renderSelect("Font Weight Header", "fontWeightHeader", [
      "normal",
      "bold",
      "100",
      "200",
      "300",
      "400",
      "500",
      "600",
      "700",
      "800",
      "900",
    ]),
    renderSelect("Font Style Header", "fontStyleHeader", [
      "normal",
      "italic",
      "oblique",
    ]),
    renderSelect("Text Align Header", "textAlignHeader", [
      "left",
      "center",
      "right",
      "justify",
    ]),
    renderSelect("Text Decoration Header", "textDecorationHeader", [
      "none",
      "underline",
      "overline",
      "line-through",
    ]),
    renderSelect("Text Transform Header", "textTransformHeader", [
      "none",
      "capitalize",
      "uppercase",
      "lowercase",
    ]),

    <h4 key="miscellaneous" style={styles.heading}>
      Miscellaneous Properties
    </h4>,
    renderSelect("Cursor", "cursor", [
      "auto",
      "pointer",
      "default",
      "text",
      "move",
      "not-allowed",
    ]),
  ];

  return (
    <div style={styles.container}>
      {allInputs.filter((input) => input !== null)}
    </div>
  );
};

const styles = {
  container: {
    padding: "10px",
    borderRadius: "8px",
    backgroundColor: "#f9f9f9",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  heading: {
    fontSize: "18px",
    margin: "10px 0",
    color: "#333",
    borderBottom: "1px solid #ccc",
    paddingBottom: "5px",
  },
  inputGroup: {
    margin: "10px 0",
  },
  label: {
    display: "block",
    marginBottom: "5px",
    fontWeight: "bold",
  },
  input: {
    width: "100%",
    padding: "3px 5px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    boxSizing: "border-box",
  },
  button: {
    marginTop: "5px",
    padding: "8px",
    backgroundColor: "#4CAF50",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  },
};

export default TableProperties;
